import axios from "axios";

const networkEvents: any[] = [];

export const getNetworkLogs = () => {
  return [...networkEvents];
};

export const uploadLogToS3 = async (data: any[], type: string) => {
  if (!data || data.length === 0) return null;
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const fileName = `${type}_logs_${Date.now()}.json`;
    const file = new File([blob], fileName, { type: "application/json" });

    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      }
    );

    return (
      response?.data?.data?.localData?.Key ||
      response?.data?.data?.decryptedData?.Key
    );
  } catch (error) {
    console.error(`Error uploading ${type} logs:`, error);
    return null;
  }
};

export const injectNetworkCapture = async () => {
  try {
    const networkMessageListener = (event: MessageEvent) => {
      if (event.source !== window) return;

      if (event.data.type === "NETWORK_REQUEST_FROM_PAGE") {
        try {
          chrome.runtime.sendMessage({
            type: "NETWORK_REQUEST",
            request: event.data.request,
          });
        } catch (error) {
          console.error(
            "Error forwarding network request to extension:",
            error
          );
        }
      }
    };

    (window as any).__network_message_listener = networkMessageListener;
    window.addEventListener("message", networkMessageListener);

    if ((window as any).__network_capture_active) {
      return;
    }

    (window as any).__network_capture_active = true;

    const interceptedRequestIds = new Set<string>();

    const generateRequestId = () =>
      `network-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const extractDomain = (url: string) => {
      try {
        return new URL(url).hostname;
      } catch {
        return "unknown";
      }
    };

    const extractFilename = (url: string) => {
      try {
        const pathname = new URL(url).pathname;
        return pathname.split("/").pop() || pathname || "unknown";
      } catch {
        return "unknown";
      }
    };

    const determineRequestType = (
      url: string,
      method: string,
      initiatorType?: string
    ) => {
      if (url.startsWith("ws:") || url.startsWith("wss:")) {
        return "WS";
      }

      const urlLower = url.toLowerCase();

      if (initiatorType) {
        switch (initiatorType) {
          case "xmlhttprequest":
          case "fetch":
            return "Fetch/XHR";
          case "websocket":
            return "WS";
          case "script":
            return "JS";
          case "img":
          case "image":
            return "Media";
          case "link":
            if (urlLower.includes(".css")) return "CSS";
            if (/\.(woff|woff2|ttf|otf|eot)$/i.test(urlLower)) return "Font";
            return "Other";
          case "css":
            return "CSS";
          case "document":
            return "Doc";
          case "other":
            return "Other";
        }
      }

      if (
        url.includes("/api/") ||
        url.includes("/graphql") ||
        url.includes("/rest/") ||
        method !== "GET"
      ) {
        return "Fetch/XHR";
      } else if (urlLower.includes(".css")) {
        return "CSS";
      } else if (urlLower.includes(".js")) {
        return "JS";
      } else if (/\.(png|jpg|jpeg|gif|svg|webp|ico|bmp)$/i.test(urlLower)) {
        return "Media";
      } else if (/\.(woff|woff2|ttf|otf|eot)$/i.test(urlLower)) {
        return "Font";
      } else if (urlLower.includes(".html")) {
        return "Doc";
      } else {
        return "Other";
      }
    };

    const sendNetworkRequest = (requestData: any) => {
      interceptedRequestIds.add(requestData.fullUrl + requestData.timestamp);
      networkEvents.push(requestData);
      try {
        window.postMessage(
          {
            type: "NETWORK_REQUEST_FROM_PAGE",
            request: requestData,
          },
          "*"
        );
      } catch (error) {
        console.error("Error in sendNetworkRequest:", error);
      }
    };

    const originalFetch = window.fetch;
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
    const OriginalWebSocket = window.WebSocket;

    // ----------------------------------------------------------------------
    // ENHANCED FETCH INTERCEPTOR
    // ----------------------------------------------------------------------
    window.fetch = function (...args) {
      const url = args[0] instanceof Request ? args[0].url : String(args[0]);
      const options = args[0] instanceof Request ? {} : args[1] || {};
      const method =
        args[0] instanceof Request ? args[0].method : options.method || "GET";

      const requestId = generateRequestId();
      const startTime = performance.now();
      const timestamp = Date.now();

      let requestHeaders = {};
      if (args[0] instanceof Request) {
        requestHeaders = Object.fromEntries(args[0].headers.entries());
      } else if (options.headers) {
        if (options.headers instanceof Headers) {
          requestHeaders = Object.fromEntries(options.headers.entries());
        } else {
          requestHeaders = options.headers;
        }
      }

      let requestPayload: any = null;

      if (args[0] instanceof Request) {
        try {
          const requestClone = args[0].clone();
          requestClone
            .text()
            .then((bodyText) => {
              if (
                bodyText &&
                (bodyText.trim().startsWith("{") ||
                  bodyText.trim().startsWith("["))
              ) {
                try {
                  requestPayload = JSON.parse(bodyText);
                } catch {
                  requestPayload =
                    bodyText.length > 10000
                      ? bodyText.substring(0, 10000) + "... [truncated]"
                      : bodyText;
                }
              } else {
                requestPayload =
                  bodyText.length > 10000
                    ? bodyText.substring(0, 10000) + "... [truncated]"
                    : bodyText;
              }
            })
            .catch(() => {
              requestPayload = "[Request body - could not read]";
            });
        } catch (e) {
          requestPayload = "[Request body - could not clone]";
        }
      } else if (options.body) {
        try {
          if (typeof options.body === "string") {
            if (
              options.body.trim().startsWith("{") ||
              options.body.trim().startsWith("[")
            ) {
              try {
                requestPayload = JSON.parse(options.body);
              } catch {
                requestPayload =
                  options.body.length > 10000
                    ? options.body.substring(0, 10000) + "... [truncated]"
                    : options.body;
              }
            } else {
              requestPayload =
                options.body.length > 10000
                  ? options.body.substring(0, 10000) + "... [truncated]"
                  : options.body;
            }
          } else if (options.body instanceof FormData) {
            const entries: string[] = [];
            try {
              (options.body as FormData).forEach((value, key) => {
                entries.push(`${key}: ${value}`);
              });
              requestPayload = `[FormData]\n${entries.join("\n")}`;
            } catch {
              requestPayload = "[FormData object]";
            }
          } else if (options.body instanceof URLSearchParams) {
            requestPayload = options.body.toString();
          } else if (options.body instanceof Blob) {
            requestPayload = `[Blob - ${options.body.size} bytes]`;
          } else {
            requestPayload = "[Binary or complex body]";
          }
        } catch (e) {
          requestPayload = "[Could not read request body]";
        }
      }

      const fetchPromise = originalFetch.apply(this, args);

      fetchPromise
        .then(async (response) => {
          const endTime = performance.now();
          const duration = Math.round(endTime - startTime);

          let responseContent: any = "";
          let responseHeaders = {};

          try {
            responseHeaders = Object.fromEntries(response.headers.entries());
            const contentType = response.headers.get("content-type") || "";

            if (
              contentType.includes("application/json") ||
              contentType.includes("text/")
            ) {
              const responseClone = response.clone();
              const responseText = await responseClone.text();

              // UPDATED: Handle double-encoded JSON
              if (contentType.includes("application/json") && responseText) {
                try {
                  let parsed = JSON.parse(responseText);
                  // Keep parsing if result is still a string
                  while (typeof parsed === "string") {
                    try {
                      parsed = JSON.parse(parsed);
                    } catch {
                      break;
                    }
                  }
                  responseContent = parsed;
                } catch {
                  responseContent = responseText;
                }
              } else {
                responseContent = responseText;
              }

              if (
                typeof responseContent === "string" &&
                responseContent.length > 10000
              ) {
                responseContent =
                  responseContent.substring(0, 10000) + "... [truncated]";
              }
            } else if (
              contentType.includes("image/") ||
              contentType.includes("video/")
            ) {
              responseContent = `[Binary Content: ${contentType}]`;
            } else {
              responseContent = `[Response - ${contentType || "unknown type"}]`;
            }
          } catch (e) {
            responseContent = `HTTP ${response.status} ${response.statusText} [Body read error]`;
          }

          const requestData = {
            id: requestId,
            name: extractFilename(url),
            fullUrl: url,
            method: method,
            status: response.status,
            statusText: response.statusText,
            domain: extractDomain(url),
            type: "Fetch/XHR",
            headers: {
              request: requestHeaders,
              response: responseHeaders,
            },
            requestPayload: requestPayload || "[No payload]",
            responseContent: responseContent,
            timestamp: timestamp,
            startTime: startTime,
            duration: duration,
            time: `${duration}ms`,
            size:
              parseInt(response.headers.get("content-length") || "0") ||
              (typeof responseContent === "string"
                ? responseContent.length
                : JSON.stringify(responseContent).length),
            error: response.status >= 400,
          };

          sendNetworkRequest(requestData);
        })
        .catch((error) => {
          const endTime = performance.now();
          const duration = Math.round(endTime - startTime);

          const requestData = {
            id: requestId,
            name: extractFilename(url),
            fullUrl: url,
            method: method,
            status: 0,
            statusText: "Network Error",
            domain: extractDomain(url),
            type: "Fetch/XHR",
            headers: {
              request: requestHeaders,
              response: {},
            },
            requestPayload: requestPayload || "[No payload]",
            responseContent: `Network Error: ${error.message || error}`,
            timestamp: timestamp,
            startTime: startTime,
            duration: duration,
            time: `${duration}ms`,
            size: 0,
            error: true,
          };

          sendNetworkRequest(requestData);
        });

      return fetchPromise;
    };

    // ----------------------------------------------------------------------
    // ENHANCED XHR INTERCEPTOR WITH COMPLETE EVENT HANDLING
    // ----------------------------------------------------------------------
    (XMLHttpRequest.prototype as any).open = function (
      method: any,
      url: any,
      ...args: any[]
    ) {
      (this as any)._captureMethod = method;
      (this as any)._captureUrl = url;
      (this as any)._captureStartTime = performance.now();
      (this as any)._captureTimestamp = Date.now();
      (this as any)._captureId = generateRequestId();
      (this as any)._captureRequestHeaders = {};
      return (originalXHROpen as any).apply(this, arguments);
    };

    (XMLHttpRequest.prototype as any).setRequestHeader = function (
      header: string,
      value: string
    ) {
      if (!(this as any)._captureRequestHeaders) {
        (this as any)._captureRequestHeaders = {};
      }
      (this as any)._captureRequestHeaders[header] = value;
      return (originalSetRequestHeader as any).apply(this, arguments);
    };

    (XMLHttpRequest.prototype as any).send = function (...args: any[]) {
      const xhr = this;
      const url = (xhr as any)._captureUrl;
      const method = (xhr as any)._captureMethod || "GET";
      const startTime = (xhr as any)._captureStartTime || performance.now();
      const timestamp = (xhr as any)._captureTimestamp || Date.now();
      const requestId = (xhr as any)._captureId;
      const requestHeaders = (xhr as any)._captureRequestHeaders || {};

      let requestPayload: any = null;
      if (args.length > 0 && args[0] != null) {
        try {
          if (typeof args[0] === "string") {
            if (
              args[0].trim().startsWith("{") ||
              args[0].trim().startsWith("[")
            ) {
              try {
                requestPayload = JSON.parse(args[0]);
              } catch {
                requestPayload =
                  args[0].length > 10000
                    ? args[0].substring(0, 10000) + "... [truncated]"
                    : args[0];
              }
            } else {
              requestPayload =
                args[0].length > 10000
                  ? args[0].substring(0, 10000) + "... [truncated]"
                  : args[0];
            }
          } else if (args[0] instanceof FormData) {
            const entries: string[] = [];
            try {
              (args[0] as FormData).forEach((value, key) => {
                entries.push(`${key}: ${value}`);
              });
              requestPayload = `[FormData]\n${entries.join("\n")}`;
            } catch {
              requestPayload = "[FormData object]";
            }
          } else if (args[0] instanceof URLSearchParams) {
            requestPayload = args[0].toString();
          } else if (args[0] instanceof Blob) {
            requestPayload = `[Blob - ${args[0].size} bytes]`;
          } else {
            requestPayload = "[Binary or complex body]";
          }
        } catch (e) {
          requestPayload = "[Could not read request body]";
        }
      }

      const handleComplete = () => {
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);

        let responseContent: any = "";
        let responseHeaders: any = {};

        try {
          const headerString = xhr.getAllResponseHeaders();
          headerString.split("\r\n").forEach((line) => {
            const [key, value] = line.split(": ");
            if (key) responseHeaders[key.toLowerCase()] = value;
          });
        } catch (e) {
          responseHeaders = {};
        }

        try {
          if (
            !xhr.responseType ||
            xhr.responseType === "" ||
            xhr.responseType === "text"
          ) {
            responseContent = xhr.responseText || "";

            // UPDATED: Handle double-encoded JSON
            if (
              responseContent.trim().startsWith("{") ||
              responseContent.trim().startsWith("[")
            ) {
              try {
                let parsed = JSON.parse(responseContent);
                // Keep parsing if result is still a string
                while (typeof parsed === "string") {
                  try {
                    parsed = JSON.parse(parsed);
                  } catch {
                    break;
                  }
                }
                responseContent = parsed;
              } catch {
                // Keep as string if parse fails
              }
            }

            if (
              typeof responseContent === "string" &&
              responseContent.length > 10000
            ) {
              responseContent =
                responseContent.substring(0, 10000) + "... [truncated]";
            }
          } else if (xhr.responseType === "json") {
            responseContent = xhr.response;
          } else if (xhr.responseType === "document") {
            responseContent = "[XML/HTML Document]";
          } else {
            responseContent = `[Binary Data: ${xhr.responseType}]`;
          }
        } catch (e) {
          responseContent = "[Error reading response]";
        }

        const requestData = {
          id: requestId,
          name: extractFilename(url),
          fullUrl: url,
          method: method,
          status: xhr.status,
          statusText: xhr.statusText,
          domain: extractDomain(url),
          type: "Fetch/XHR",
          headers: {
            request: requestHeaders,
            response: responseHeaders,
          },
          requestPayload: requestPayload || "[No payload]",
          responseContent: responseContent,
          timestamp: timestamp,
          startTime: startTime,
          duration: duration,
          time: `${duration}ms`,
          size:
            typeof responseContent === "string"
              ? responseContent.length
              : JSON.stringify(responseContent).length,
          error: xhr.status >= 400 || xhr.status === 0,
        };

        sendNetworkRequest(requestData);
      };

      const originalReadyStateChange = xhr.onreadystatechange;
      xhr.onreadystatechange = function (event) {
        if (xhr.readyState === 4) {
          handleComplete();
        }
        if (originalReadyStateChange) {
          originalReadyStateChange.call(this, event);
        }
      };

      xhr.addEventListener("load", () => {
        if (!(xhr as any)._captureHandled) {
          (xhr as any)._captureHandled = true;
          handleComplete();
        }
      });

      xhr.addEventListener("error", () => {
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);

        const requestData = {
          id: requestId,
          name: extractFilename(url),
          fullUrl: url,
          method: method,
          status: 0,
          statusText: "Network Error",
          domain: extractDomain(url),
          type: "Fetch/XHR",
          headers: {
            request: requestHeaders,
            response: {},
          },
          requestPayload: requestPayload || "[No payload]",
          responseContent: "Network Error: Request failed",
          timestamp: timestamp,
          startTime: startTime,
          duration: duration,
          time: `${duration}ms`,
          size: 0,
          error: true,
        };

        sendNetworkRequest(requestData);
      });

      xhr.addEventListener("abort", () => {
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);

        const requestData = {
          id: requestId,
          name: extractFilename(url),
          fullUrl: url,
          method: method,
          status: 0,
          statusText: "Aborted",
          domain: extractDomain(url),
          type: "Fetch/XHR",
          headers: {
            request: requestHeaders,
            response: {},
          },
          requestPayload: requestPayload || "[No payload]",
          responseContent: "Request Aborted",
          timestamp: timestamp,
          startTime: startTime,
          duration: duration,
          time: `${duration}ms`,
          size: 0,
          error: true,
        };

        sendNetworkRequest(requestData);
      });

      xhr.addEventListener("timeout", () => {
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);

        const requestData = {
          id: requestId,
          name: extractFilename(url),
          fullUrl: url,
          method: method,
          status: 0,
          statusText: "Timeout",
          domain: extractDomain(url),
          type: "Fetch/XHR",
          headers: {
            request: requestHeaders,
            response: {},
          },
          requestPayload: requestPayload || "[No payload]",
          responseContent: "Request Timeout",
          timestamp: timestamp,
          startTime: startTime,
          duration: duration,
          time: `${duration}ms`,
          size: 0,
          error: true,
        };

        sendNetworkRequest(requestData);
      });

      return (originalXHRSend as any).apply(this, arguments);
    };

    // ----------------------------------------------------------------------
    // WEBSOCKET INTERCEPTOR
    // ----------------------------------------------------------------------
    window.WebSocket = function (
      url: string | URL,
      protocols?: string | string[]
    ) {
      const ws = new OriginalWebSocket(url, protocols);
      const startTime = performance.now();
      const timestamp = Date.now();
      const requestId = generateRequestId();
      const urlStr = url.toString();

      const wsData = {
        id: requestId,
        name: extractFilename(urlStr),
        fullUrl: urlStr,
        method: "WS",
        status: 101,
        statusText: "Pending Connection",
        domain: extractDomain(urlStr),
        type: "WS",
        headers: {
          request: { protocols: protocols || "default" },
          response: {},
        },
        requestPayload: null,
        responseContent: "WebSocket Connecting...",
        timestamp: timestamp,
        startTime: startTime,
        duration: 0,
        time: "0ms",
        size: 0,
        error: false,
      };

      ws.addEventListener("open", () => {
        const openTime = performance.now();
        sendNetworkRequest({
          ...wsData,
          status: 101,
          statusText: "Connected",
          responseContent: "WebSocket Connection Established",
          duration: Math.round(openTime - startTime),
          time: `${Math.round(openTime - startTime)}ms`,
          timestamp: Date.now(),
        });
      });

      ws.addEventListener("error", () => {
        const errorTime = performance.now();
        sendNetworkRequest({
          ...wsData,
          status: 0,
          statusText: "Error",
          error: true,
          responseContent: "WebSocket Connection Failed",
          timestamp: Date.now(),
          duration: Math.round(errorTime - startTime),
          time: `${Math.round(errorTime - startTime)}ms`,
        });
      });

      ws.addEventListener("close", (event) => {
        const closeTime = performance.now();
        sendNetworkRequest({
          ...wsData,
          status: event.code,
          statusText: "Closed",
          responseContent: `WebSocket Closed: ${event.reason || "No reason"}`,
          timestamp: Date.now(),
          duration: Math.round(closeTime - startTime),
          time: `${Math.round(closeTime - startTime)}ms`,
        });
      });

      return ws;
    } as any;

    window.WebSocket.prototype = OriginalWebSocket.prototype;
    (window.WebSocket as any).CONNECTING = OriginalWebSocket.CONNECTING;
    (window.WebSocket as any).OPEN = OriginalWebSocket.OPEN;
    (window.WebSocket as any).CLOSING = OriginalWebSocket.CLOSING;
    (window.WebSocket as any).CLOSED = OriginalWebSocket.CLOSED;

    // ----------------------------------------------------------------------
    // PERFORMANCE OBSERVER (Assets Only - No Fetch/XHR/WS)
    // ----------------------------------------------------------------------
    const observeAllNetworkRequests = () => {
      if ("PerformanceObserver" in window) {
        const perfObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry: any) => {
            if (
              entry.entryType === "navigation" ||
              entry.entryType === "resource"
            ) {
              if (
                entry.initiatorType === "fetch" ||
                entry.initiatorType === "xmlhttprequest" ||
                entry.initiatorType === "websocket"
              ) {
                return;
              }

              const checkKey = entry.name + Math.round(entry.responseEnd);
              if (interceptedRequestIds.has(checkKey)) {
                return;
              }

              const requestId = generateRequestId();
              let initiatorType = "other";
              if (entry.initiatorType) {
                initiatorType = entry.initiatorType;
              }

              const requestData = {
                id: requestId,
                name: extractFilename(entry.name),
                fullUrl: entry.name,
                method: "GET",
                status: entry.responseStatus || 200,
                statusText: entry.responseStatus >= 400 ? "Error" : "OK",
                domain: extractDomain(entry.name),
                type: determineRequestType(entry.name, "GET", initiatorType),
                headers: { request: {}, response: {} },
                requestPayload: null,
                responseContent:
                  "[Static Asset - Body not captured in Performance API]",
                timestamp: Math.round(entry.responseEnd),
                startTime: entry.startTime,
                duration: Math.round(entry.duration || 0),
                time: entry.duration
                  ? `${Math.round(entry.duration)}ms`
                  : "0ms",
                size: entry.transferSize || entry.encodedBodySize || 0,
                error: entry.responseStatus >= 400,
              };

              sendNetworkRequest(requestData);
            }
          });
        });

        try {
          perfObserver.observe({
            entryTypes: ["navigation", "resource"],
            buffered: true,
          });
          return perfObserver;
        } catch (e) {
          console.warn(
            "Performance Observer not supported for resource timing:",
            e
          );
        }
      }

      return null;
    };

    const perfObserver = observeAllNetworkRequests();

    (window as any).__network_cleanup = () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
      XMLHttpRequest.prototype.setRequestHeader = originalSetRequestHeader;
      window.WebSocket = OriginalWebSocket;

      if (perfObserver) {
        perfObserver.disconnect();
      }

      delete (window as any).__network_cleanup;
      delete (window as any).__network_capture_active;
      interceptedRequestIds.clear();
      networkEvents.length = 0;
    };
  } catch (error) {
    console.error("Error injecting network capture:", error);
  }
};

export const removeNetworkCapture = async () => {
  try {
    if ((window as any).__network_message_listener) {
      window.removeEventListener(
        "message",
        (window as any).__network_message_listener
      );
      delete (window as any).__network_message_listener;
    }

    if ((window as any).__network_cleanup) {
      (window as any).__network_cleanup();
    }

    if ((window as any).__network_capture_active) {
      delete (window as any).__network_capture_active;
    }
    networkEvents.length = 0;
  } catch (error) {
    console.error("Error removing network capture:", error);
  }
};

// Curl generator and other utility functions remain the same
export const generateCurlCommand = (request: any): string => {
  const { method = "GET", fullUrl, headers, requestPayload } = request;

  let curlCommand = `curl '${fullUrl}'`;

  if (method !== "GET") {
    curlCommand += ` \\\n  -X ${method}`;
  }

  if (headers?.request) {
    Object.entries(headers.request).forEach(([key, value]) => {
      curlCommand += ` \\\n  -H '${key}: ${value}'`;
    });
  }

  if (requestPayload && requestPayload !== "[No payload]" && method !== "GET") {
    let payloadStr = requestPayload;

    if (typeof requestPayload === "object") {
      try {
        payloadStr = JSON.stringify(requestPayload);
      } catch (e) {
        payloadStr = String(requestPayload);
      }
    } else if (typeof requestPayload === "string") {
      try {
        const parsed = JSON.parse(requestPayload);
        payloadStr = JSON.stringify(parsed);
      } catch (e) {
        // Keep original if not JSON
      }
    }

    if (payloadStr.includes("'")) {
      curlCommand += ` \\\n  --data-raw "${payloadStr.replace(/"/g, '\\"')}"`;
    } else {
      curlCommand += ` \\\n  --data-raw '${payloadStr}'`;
    }
  }

  curlCommand += " \\\n  --compressed";

  return curlCommand;
};

export const generateFetchCommand = (request: any): string => {
  const { method = "GET", fullUrl, headers, requestPayload } = request;

  const headersObj: any = {};
  if (headers?.request) {
    Object.entries(headers.request).forEach(([key, value]) => {
      headersObj[key] = value;
    });
  }

  const fetchOptions: any = {
    method: method,
    headers: headersObj,
  };

  if (requestPayload && requestPayload !== "[No payload]" && method !== "GET") {
    if (typeof requestPayload === "object") {
      fetchOptions.body = JSON.stringify(requestPayload);
    } else if (typeof requestPayload === "string") {
      try {
        const parsed = JSON.parse(requestPayload);
        fetchOptions.body = JSON.stringify(parsed);
      } catch (e) {
        fetchOptions.body = requestPayload;
      }
    } else {
      fetchOptions.body = String(requestPayload);
    }
  }

  const code = `fetch("${fullUrl}", ${JSON.stringify(fetchOptions, null, 2)})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`;

  return code;
};

export const generatePowershellCommand = (request: any): string => {
  const { method = "GET", fullUrl, headers, requestPayload } = request;

  let psCommand = `Invoke-WebRequest -Uri '${fullUrl}' \`\n  -Method ${method}`;

  if (headers?.request) {
    const headersList = Object.entries(headers.request)
      .map(([key, value]) => `    "${key}"="${value}"`)
      .join("\n");
    psCommand += ` \`\n  -Headers @{\n${headersList}\n  }`;
  }

  if (requestPayload && requestPayload !== "[No payload]" && method !== "GET") {
    let bodyStr = requestPayload;

    if (typeof requestPayload === "object") {
      try {
        bodyStr = JSON.stringify(requestPayload);
      } catch (e) {
        bodyStr = String(requestPayload);
      }
    }

    psCommand += ` \`\n  -Body '${String(bodyStr).replace(/'/g, "''")}'`;
  }

  return psCommand;
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy:", err);
    return false;
  }
};
